import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FinancialRecord } from "@akfdev-com/shared";
import { toGbpDisplay } from "./gbp";
import { deriveStatus } from "./status";
import {
  applyRecordFilter,
  groupByRecordType,
  recordTypeLabel,
} from "./recordFilter";
import { deriveDescription, deriveInvoiceNumberCell } from "./recordDisplay";
import { NewRecordMenu } from "./NewRecordMenu";
import { financialRecordsApi, getApiOwnerId } from "../../services/api";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { useUIErrorDisplay } from "../../components/providers";
import type { RecordFilterModel } from "./types";

interface FinancialRecordsTableProps {
  records: FinancialRecord[];
  filter: RecordFilterModel;
  onFilterChange: (f: RecordFilterModel) => void;
  onAfterDelete?: () => Promise<void>;
  contact?: any; // Contact type
  loading?: boolean;
  empty?: boolean;
}

const recordTypeOptions: Array<{
  value: RecordFilterModel["type"];
  label: string;
}> = [
  { value: "All", label: "All" },
  { value: "Invoices", label: "Invoices" },
  { value: "Bills", label: "Bills" },
  { value: "Service Charges", label: "Service Charges" },
  { value: "Payments", label: "Payments" },
];

const statusOptions: Array<{
  value: RecordFilterModel["status"];
  label: string;
}> = [
  { value: null, label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "partial", label: "Partial" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
];

const statusBadgeColors: Record<
  "pending" | "partial" | "paid" | "overdue",
  string
> = {
  pending: "bg-yellow-100 text-yellow-800",
  partial: "bg-blue-100 text-blue-800",
  paid: "bg-green-100 text-green-800",
  overdue: "bg-red-100 text-red-800",
};

/**
 * Financial Records Table
 *
 * Renders a table of financial records (invoices, bills, service charges, payments) with:
 * - Type filter chips (All/Invoices/Bills/Service Charges/Payments)
 * - Status filter dropdown
 * - Columns: Date, Type, Invoice #, Description, Amount, Status, Actions
 * - Records grouped by type, ordered by date descending within each group
 * - Per-row edit and delete actions (added in Task 4.2)
 *
 * Validates: Requirements 2.2, 2.3, 2.4, 2.7, 2.8, 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 8.5, 9.6
 */
export const FinancialRecordsTable: React.FC<FinancialRecordsTableProps> = ({
  records,
  filter,
  onFilterChange,
  onAfterDelete,
  contact,
  loading,
  empty,
}) => {
  const now = new Date();
  const navigate = useNavigate();
  const { showToast } = useUIErrorDisplay();
  const ownerId = getApiOwnerId();

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<FinancialRecord | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);

  // Apply filter and group by record type
  const filteredRecords = useMemo(
    () => applyRecordFilter(records, filter, now),
    [records, filter, now],
  );

  const groupedRecords = useMemo(
    () => groupByRecordType(filteredRecords),
    [filteredRecords],
  );

  const handleTypeChange = (type: RecordFilterModel["type"]) => {
    onFilterChange({ ...filter, type });
  };

  const handleStatusChange = (status: RecordFilterModel["status"]) => {
    onFilterChange({ ...filter, status });
  };

  const handleEditClick = (record: FinancialRecord) => {
    navigate(
      `/dashboard/financial-records/${record.id}?buildingId=${record.buildingId}`,
    );
  };

  const handleDeleteClick = (record: FinancialRecord) => {
    setRecordToDelete(record);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!recordToDelete || !ownerId) return;

    try {
      setIsDeleting(true);
      await financialRecordsApi.delete(
        recordToDelete.id,
        recordToDelete.buildingId,
      );

      setDeleteDialogOpen(false);
      setRecordToDelete(null);

      // Trigger re-fetch via callback if provided
      if (onAfterDelete) {
        // Small delay to ensure deletion is processed
        setTimeout(() => {
          onAfterDelete();
        }, 500);
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to delete financial record";
      showToast({
        message: errorMessage,
        errorId: `delete-error-${recordToDelete.id}`,
        severity: "ERROR",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="bg-white shadow sm:rounded-lg">
      {/* Header with toolbar */}
      <div className="px-6 py-5 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-900">
            Financial Records
          </h2>
          {contact && (
            <NewRecordMenu
              contact={contact}
              ownerId={getApiOwnerId() || undefined}
            />
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-4">
          {/* Type filter chips */}
          <div className="flex flex-wrap gap-2">
            {recordTypeOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleTypeChange(opt.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
                  filter.type === opt.value
                    ? "bg-gray-900 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Status filter dropdown */}
          <div className="flex items-center gap-2">
            <label
              htmlFor="status-filter"
              className="text-sm font-medium text-gray-700"
            >
              Status:
            </label>
            <select
              id="status-filter"
              value={filter.status ?? ""}
              onChange={(e) =>
                handleStatusChange(
                  (e.target.value || null) as RecordFilterModel["status"],
                )
              }
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {statusOptions.map((opt) => (
                <option key={opt.value ?? "__null__"} value={opt.value ?? ""}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table or empty state */}
      {loading ? (
        <div className="px-6 py-8 text-sm text-gray-400">Loading…</div>
      ) : filteredRecords.length === 0 ? (
        <div className="px-6 py-8 text-sm text-gray-400 italic">
          No financial records found.
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {Object.entries(groupedRecords).map(
            ([recordType, recordsInGroup]) => (
              <div key={recordType} className="px-6 py-4">
                {/* Group header */}
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  {recordTypeLabel(recordType as any)}
                </h3>

                {/* Table */}
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th
                        className="pb-2 pr-4 text-left text-xs font-bold uppercase text-gray-400"
                        style={{ fontSize: "10px", color: "#9ca3af" }}
                      >
                        Date
                      </th>
                      <th
                        className="pb-2 pr-4 text-left text-xs font-bold uppercase text-gray-400"
                        style={{ fontSize: "10px", color: "#9ca3af" }}
                      >
                        Type
                      </th>
                      <th
                        className="pb-2 pr-4 text-left text-xs font-bold uppercase text-gray-400"
                        style={{ fontSize: "10px", color: "#9ca3af" }}
                      >
                        Invoice #
                      </th>
                      <th
                        className="pb-2 pr-4 text-left text-xs font-bold uppercase text-gray-400"
                        style={{ fontSize: "10px", color: "#9ca3af" }}
                      >
                        Description
                      </th>
                      <th
                        className="pb-2 pr-4 text-right text-xs font-bold uppercase text-gray-400"
                        style={{ fontSize: "10px", color: "#9ca3af" }}
                      >
                        Amount
                      </th>
                      <th
                        className="pb-2 pr-4 text-left text-xs font-bold uppercase text-gray-400"
                        style={{ fontSize: "10px", color: "#9ca3af" }}
                      >
                        Status
                      </th>
                      <th
                        className="pb-2 pr-4 text-left text-xs font-bold uppercase text-gray-400"
                        style={{ fontSize: "10px", color: "#9ca3af" }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {recordsInGroup.map((record) => {
                      const status = deriveStatus(
                        record.amountPaid || 0,
                        record.amount || 0,
                        record.dueDate || new Date().toISOString(),
                        now,
                      );
                      const description = deriveDescription(record);
                      const invoiceCell = deriveInvoiceNumberCell(record);

                      return (
                        <tr key={record.id} className="hover:bg-gray-50">
                          <td className="py-2 pr-4 text-gray-600">
                            {record.date
                              ? new Date(record.date).toLocaleDateString(
                                  "en-GB",
                                  {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                  },
                                )
                              : "—"}
                          </td>
                          <td className="py-2 pr-4 text-gray-600">
                            {recordTypeLabel(record.type)}
                          </td>
                          <td className="py-2 pr-4 text-gray-600">
                            {invoiceCell.empty ? "—" : invoiceCell.value}
                          </td>
                          <td className="py-2 pr-4 text-gray-700 max-w-sm">
                            {description.empty ? (
                              "—"
                            ) : (
                              <>
                                {description.text}
                                {description.truncated && "…"}
                              </>
                            )}
                          </td>
                          <td className="py-2 pr-4 text-right font-medium text-gray-900">
                            £{toGbpDisplay(record.amount || 0)}
                          </td>
                          <td className="py-2">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusBadgeColors[status]}`}
                            >
                              {status}
                            </span>
                          </td>
                          <td className="py-2 pr-4 text-gray-600">
                            {/* Edit and Delete action buttons */}
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => handleEditClick(record)}
                                aria-label={`Edit ${recordTypeLabel(
                                  record.type,
                                ).toLowerCase()}`}
                                className="text-gray-600 hover:text-primary-600 transition-colors p-1"
                                title="Edit record"
                              >
                                ✎
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteClick(record)}
                                aria-label={`Delete ${recordTypeLabel(
                                  record.type,
                                ).toLowerCase()}`}
                                className="text-gray-600 hover:text-red-600 transition-colors p-1"
                                title="Delete record"
                              >
                                🗑
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ),
          )}

          {/* Total (filtered) footer row */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <table className="min-w-full text-sm">
              <tbody>
                <tr>
                  <td
                    colSpan={4}
                    className="py-3 pr-4 text-left text-sm font-semibold text-gray-900"
                  >
                    Total (filtered)
                  </td>
                  <td className="py-3 pr-4 text-right font-bold text-gray-900">
                    £
                    {toGbpDisplay(
                      filteredRecords.reduce(
                        (sum, record) => sum + (record.amount || 0),
                        0,
                      ),
                    )}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        title="Delete Financial Record"
        message={`Are you sure you want to delete this ${
          recordToDelete ? recordTypeLabel(recordToDelete.type) : "record"
        }? This action cannot be undone.`}
        confirmText={isDeleting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        onConfirm={handleConfirmDelete}
        onClose={() => {
          if (!isDeleting) {
            setDeleteDialogOpen(false);
            setRecordToDelete(null);
          }
        }}
      />
    </div>
  );
};
